/*
Copyright 2025 TekNix Corporation

This software is proprietary and confidential to TekNix Corporation.
All rights reserved. No part of this software may be reproduced, distributed,
or transmitted in any form or by any means, including photocopying, recording,
or other electronic or mechanical methods, without the prior written permission
of TekNix Corporation, except in the case of brief quotations embodied in
critical reviews and certain other noncommercial uses permitted by copyright law.

For permission requests, write to TekNix Corporation at the address below:
TekNix Corporation
Legal Department

UNAUTHORIZED REPRODUCTION OR DISTRIBUTION IS STRICTLY PROHIBITED.
*/

import type { HttpResponse } from '../http';

/**
 * Contact relationship types
 */
export enum ContactRelationship {
  /** System suggested contact */
  Suggested = 'suggested',
  /** User manually added */
  Added = 'added',
  /** Mutual contact (both added each other) */
  Mutual = 'mutual',
  /** Blocked contact */
  Blocked = 'blocked',
}

/**
 * Contact metadata from server
 */
export type ContactMetadata = {
  /** Common groups/rooms */
  mutualRooms?: string[];
  /** Mutual friends count */
  mutualFriendsCount?: number;
  /** Reason for suggestion */
  suggestionReason?: 'mutual_friends' | 'frequent_interaction' | 'same_workspace' | 'imported';
  /** Custom tags */
  tags?: string[];
  /** Phone number if available */
  phoneNumber?: string;
  /** Email if available */
  email?: string;
  /** Job title or role */
  jobTitle?: string;
  /** Organization or company */
  organization?: string;
  /** Any additional server data */
  [key: string]: any;
};

/**
 * Serializable contact data for storage
 */
export type ContactData = {
  id: string;
  channel_slug: string;
  logo: string;
  name: string;
  is_online: boolean;
  description: string;
  active: string;
  created_at: string;
  updated_at: string;
  slug: string;
};

/**
 * API response for fetching contacts
 */
export type FetchContactsResponse = HttpResponse<Array<ContactData>>;

/**
 * API response for fetching suggestions
 */
export type FetchSuggestionsResponse = {
  suggestions: Array<{
    userId: string;
    displayName?: string;
    avatarUrl?: string;
    metadata: ContactMetadata;
    description?: string;
  }>;
  cursor?: string;
  total?: number;
};

/**
 * Options for filtering contacts
 */
export type ContactFilterOptions = {
  /** Filter by relationship type */
  relationship?: ContactRelationship;
  /** Filter by online status */
  onlineOnly?: boolean;
  /** Filter by tags */
  tags?: string[];
  /** Search query for display name or user ID */
  query?: string;
  /** Sort order */
  sortBy?: 'displayName' | 'lastInteraction' | 'addedAt';
  /** Sort direction */
  sortDirection?: 'asc' | 'desc';
};

/**
 * Statistics about contacts
 */
export type ContactsStats = {
  total: number;
  online: number;
  byRelationship: Record<ContactRelationship, number>;
  recentlyActive: number;
};
